
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import {
  required,
  minLength,
  helpers,
} from '@vuelidate/validators';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, RenameActionResult } from './types';

// Custom validator for alphanumeric with spaces and accents
const alphanumericWithSpaces = helpers.regex(/^[\w\sàâäéèêëïîôùûüÿçÀÂÄÉÈÊËÏÎÔÙÛÜŸÇ-]+$/);

export default defineComponent({
  name: 'RenameAction',

  components: {
    Dialog,
    InputText,
    SpmButton,
  },

  props: {
    /**
     * Dialog title
     */
    dialogTitle: {
      type: String,
      default: '',
    },

    /**
     * Input field label
     */
    inputLabel: {
      type: String,
      default: '',
    },

    /**
     * Cancel button label
     */
    cancelLabel: {
      type: String,
      default: '',
    },

    /**
     * Confirm button label
     */
    confirmLabel: {
      type: String,
      default: '',
    },

    /**
     * Validation error messages
     */
    validationMessages: {
      type: Object as () => {
        required?: string;
        minLength?: string;
        alphanumeric?: string;
      },

      default: () => ({}),
    },

    /**
     * Minimum length for the name
     */
    minNameLength: {
      type: Number,
      default: 3,
    },
  },

  emits: ['confirm'],

  setup(props, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const newName = ref('');
    const currentPayload = ref<ActionPayload | null>(null);

    // Validation rules
    const rules = computed(() => ({
      newName: {
        required: helpers.withMessage(
          () => props.validationMessages.required || t('myLists.manage.actions.validation.required'),
          required,
        ),
        minLength: helpers.withMessage(
          () => props.validationMessages.minLength || t('myLists.manage.actions.validation.minLength', { min: props.minNameLength }),
          minLength(props.minNameLength),
        ),
        alphanumericWithSpaces: helpers.withMessage(
          () => props.validationMessages.alphanumeric || t('myLists.manage.actions.validation.alphanumeric'),
          alphanumericWithSpaces,
        ),
      },
    }));

    const v$ = useVuelidate(rules, { newName });

    /**
     * Get the first error message for newName
     */
    const getErrorMessage = () => {
      if (v$.value.newName.$errors.length > 0) {
        return v$.value.newName.$errors[0].$message;
      }
      return '';
    };

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ActionPayload) => {
      currentPayload.value = payload;
      newName.value = payload.name;
      v$.value.$reset();
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      v$.value.$reset();
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      // Validate before confirming
      const isValid = await v$.value.$validate();
      if (!isValid) return;

      const result: RenameActionResult = {
        id: currentPayload.value.id,
        newName: newName.value.trim(),
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      isVisible,
      newName,
      close,
      confirm,
      v$,
      getErrorMessage,
    };
  },
});
